import {
  Clock,
  CheckCircle2,
  Play,
  Coffee,
  Trophy,
  ChevronRight,
} from "../../../../components/icons";
import { Match } from "../../types";
import { PeriodPhase } from "../../hooks/usePeriodManager";

interface MatchPeriodSelectorProps {
  match: Match | null;
  operatorPeriod: number;
  currentPhase: PeriodPhase;
  isOvertime: boolean;
  extraTimeSeconds: number;
  globalClock: string;
  isClockRunning: boolean;
  onTransitionToBreakQ1?: () => void;
  onTransitionToQ2?: () => void;
  onTransitionToHalftime?: () => void;
  onTransitionToQ3?: () => void;
  onTransitionToBreakQ3?: () => void;
  onTransitionToQ4?: () => void;
  onTransitionToFulltime?: () => void;
  onTransitionToOvertime?: () => void;
  onFinishMatch?: () => void;
  transitionDisabled?: boolean;
  transitionReason?: string;
  t: any;
}

export function MatchPeriodSelector({
  match,
  currentPhase,
  isOvertime,
  extraTimeSeconds,
  globalClock,
  onTransitionToBreakQ1,
  onTransitionToQ2,
  onTransitionToHalftime,
  onTransitionToQ3,
  onTransitionToBreakQ3,
  onTransitionToQ4,
  onTransitionToFulltime,
  onTransitionToOvertime,
  onFinishMatch,
  transitionDisabled = false,
  transitionReason,
  t,
}: MatchPeriodSelectorProps) {
  if (!match) return null;

  const getMatchStatus = () => {
    switch (currentPhase) {
      case "NOT_STARTED":
      case "Q1":
        return {
          stage: "q1",
          label: t("logger.period.q1InProgress", "Q1"),
          statusText: t("logger.period.inProgress", "In Progress"),
          icon: Play,
          iconColor: "text-green-600",
          bgColor: "bg-green-50",
          borderColor: "border-green-200",
          canTransition: true,
          transitionLabel: t("logger.period.endQ1", "End Q1"),
          transitionAction: onTransitionToBreakQ1,
          progress: "q1",
        };
      case "BREAK_Q1":
        return {
          stage: "break-q1",
          label: t("logger.period.breakQ1", "Q1 Break"),
          statusText: t("logger.period.break", "Break"),
          icon: Coffee,
          iconColor: "text-yellow-600",
          bgColor: "bg-yellow-50",
          borderColor: "border-yellow-200",
          canTransition: true,
          transitionLabel: t("logger.period.startQ2", "Start Q2"),
          transitionAction: onTransitionToQ2,
          progress: "break-q1",
        };
      case "Q2":
        return {
          stage: "q2",
          label: t("logger.period.q2InProgress", "Q2"),
          statusText: t("logger.period.inProgress", "In Progress"),
          icon: Play,
          iconColor: "text-blue-600",
          bgColor: "bg-blue-50",
          borderColor: "border-blue-200",
          canTransition: true,
          transitionLabel: t("logger.period.endQ2", "End Q2"),
          transitionAction: onTransitionToHalftime,
          progress: "q2",
        };
      case "HALFTIME":
        return {
          stage: "halftime",
          label: t("logger.period.halftime", "Halftime"),
          statusText: t("logger.period.break", "Break"),
          icon: Coffee,
          iconColor: "text-yellow-600",
          bgColor: "bg-yellow-50",
          borderColor: "border-yellow-200",
          canTransition: true,
          transitionLabel: t("logger.period.startQ3", "Start Q3"),
          transitionAction: onTransitionToQ3,
          progress: "halftime",
        };
      case "Q3":
        return {
          stage: "q3",
          label: t("logger.period.q3InProgress", "Q3"),
          statusText: t("logger.period.inProgress", "In Progress"),
          icon: Play,
          iconColor: "text-green-600",
          bgColor: "bg-green-50",
          borderColor: "border-green-200",
          canTransition: true,
          transitionLabel: t("logger.period.endQ3", "End Q3"),
          transitionAction: onTransitionToBreakQ3,
          progress: "q3",
        };
      case "BREAK_Q3":
        return {
          stage: "break-q3",
          label: t("logger.period.breakQ3", "Q3 Break"),
          statusText: t("logger.period.break", "Break"),
          icon: Coffee,
          iconColor: "text-yellow-600",
          bgColor: "bg-yellow-50",
          borderColor: "border-yellow-200",
          canTransition: true,
          transitionLabel: t("logger.period.startQ4", "Start Q4"),
          transitionAction: onTransitionToQ4,
          progress: "break-q3",
        };
      case "Q4":
        return {
          stage: "q4",
          label: t("logger.period.q4InProgress", "Q4"),
          statusText: t("logger.period.inProgress", "In Progress"),
          icon: Play,
          iconColor: "text-blue-600",
          bgColor: "bg-blue-50",
          borderColor: "border-blue-200",
          canTransition: true,
          transitionLabel: t("logger.period.endQ4", "End Q4"),
          transitionAction: onTransitionToFulltime,
          progress: "q4",
        };
      case "FULLTIME":
        return {
          stage: "fulltime-options",
          label: t("logger.period.regulationEnded", "Regulation Ended"),
          statusText: t("logger.period.finished", "Finished"),
          icon: Trophy,
          iconColor: "text-purple-600",
          bgColor: "bg-purple-50",
          borderColor: "border-purple-200",
          canTransition: true,
          progress: "fulltime",
          isOptions: true,
        };
      case "OVERTIME":
        return {
          stage: "overtime",
          label: t("logger.period.overtime", "Overtime"),
          statusText: t("logger.period.inProgress", "In Progress"),
          icon: Play,
          iconColor: "text-orange-600",
          bgColor: "bg-orange-50",
          borderColor: "border-orange-200",
          canTransition: true,
          progress: "overtime",
          isOptions: true,
          optionsType: "overtime-end",
        };
      case "BREAK_OT":
        return {
          stage: "break-ot",
          label: t("logger.period.breakOT", "OT Break"),
          statusText: t("logger.period.break", "Break"),
          icon: Coffee,
          iconColor: "text-yellow-600",
          bgColor: "bg-yellow-50",
          borderColor: "border-yellow-200",
          canTransition: true,
          progress: "break-ot",
          isOptions: true,
          optionsType: "overtime-end",
        };
      case "COMPLETED":
        return {
          stage: "completed",
          label: t("logger.period.matchCompleted", "Match Completed"),
          statusText: t("logger.period.finished", "Finished"),
          icon: CheckCircle2,
          iconColor: "text-slate-600",
          bgColor: "bg-slate-100",
          borderColor: "border-slate-300",
          canTransition: false,
          progress: "show-all",
        };
      default:
        return {
          stage: "unknown",
          label: t("logger.period.unknown", "Unknown"),
          statusText: "",
          icon: Clock,
          iconColor: "text-gray-600",
          bgColor: "bg-gray-50",
          borderColor: "border-gray-200",
          canTransition: false,
          progress: "q1",
        };
    }
  };

  const status = getMatchStatus();
  const StatusIcon = status.icon;
  const periodStatusTestId =
    status.stage === "fulltime-options" || status.stage === "completed"
      ? "fulltime"
      : status.stage;
  const extraClock =
    extraTimeSeconds > 0
      ? ` +${Math.floor(extraTimeSeconds / 60)}:${String(
          extraTimeSeconds % 60,
        ).padStart(2, "0")}`
      : "";
  const transitionTestId =
    status.stage === "q1"
      ? "btn-end-q1"
      : status.stage === "break-q1"
        ? "btn-start-q2"
        : status.stage === "q2"
          ? "btn-end-q2"
          : status.stage === "halftime"
            ? "btn-start-q3"
            : status.stage === "q3"
              ? "btn-end-q3"
              : status.stage === "break-q3"
                ? "btn-start-q4"
                : status.stage === "q4"
                  ? "btn-end-q4"
                  : undefined;

  const trimMs = (value: string) => value.split(".")[0] || value;

  return (
    <div className="space-y-4">
      <div
        className={`rounded-lg border-2 ${status.borderColor} ${status.bgColor} p-4 transition-all duration-300`}
        data-testid={`period-status-${periodStatusTestId}`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`${status.iconColor} animate-pulse`}>
              <StatusIcon size={24} />
            </div>
            <div>
              <div className="text-sm text-gray-500 uppercase tracking-wide">
                {status.statusText}
              </div>
              <div className="text-xl font-bold text-gray-900">
                {status.label}
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-2xl font-mono font-bold text-gray-900">
              {`${trimMs(globalClock)}${extraClock}`}
            </div>
          </div>
        </div>
      </div>

      {status.isOptions && status.stage === "fulltime-options" ? (
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={onFinishMatch || (() => {})}
            className="py-3 px-4 rounded-lg font-semibold flex items-center justify-center gap-2 bg-slate-700 hover:bg-slate-800 text-white shadow-sm"
            disabled={transitionDisabled}
            data-testid="btn-end-match-final"
          >
            <CheckCircle2 size={20} />
            <span>{t("logger.period.endMatch", "End Match")}</span>
          </button>
          <button
            onClick={onTransitionToOvertime}
            className="py-3 px-4 rounded-lg font-semibold flex items-center justify-center gap-2 bg-orange-600 hover:bg-orange-700 text-white shadow-sm"
            disabled={transitionDisabled}
            data-testid="btn-start-overtime"
          >
            <Play size={20} />
            <span>{t("logger.period.startOvertime", "Start Overtime")}</span>
          </button>
        </div>
      ) : status.isOptions && status.optionsType === "overtime-end" ? (
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={onFinishMatch}
            className="py-3 px-4 rounded-lg font-semibold flex items-center justify-center gap-2 bg-slate-700 hover:bg-slate-800 text-white shadow-sm"
            disabled={transitionDisabled}
            data-testid="btn-end-match-ot"
          >
            <CheckCircle2 size={20} />
            <span>{t("logger.period.endMatch", "End Match")}</span>
          </button>
          <button
            onClick={onTransitionToOvertime}
            className="py-3 px-4 rounded-lg font-semibold flex items-center justify-center gap-2 bg-orange-600 hover:bg-orange-700 text-white shadow-sm"
            disabled={transitionDisabled}
            data-testid="btn-continue-overtime"
          >
            <Play size={20} />
            <span>{t("logger.period.continueOT", "Another OT")}</span>
          </button>
        </div>
      ) : (
        status.transitionAction && (
          <button
            onClick={status.transitionAction}
            className={`w-full py-3 px-4 rounded-lg font-semibold flex items-center justify-center gap-2
            transition-all duration-200 shadow-sm hover:shadow-md
            ${
              status.stage === "q1" || status.stage === "q3"
                ? "bg-green-600 hover:bg-green-700 text-white"
                : status.stage === "break-q1" ||
                    status.stage === "halftime" ||
                    status.stage === "break-q3"
                  ? "bg-yellow-500 hover:bg-yellow-600 text-white"
                  : status.stage === "q2" || status.stage === "q4"
                    ? "bg-blue-600 hover:bg-blue-700 text-white"
                    : "bg-gray-400 text-white"
            }`}
            data-testid={transitionTestId}
            disabled={transitionDisabled}
          >
            <span>{status.transitionLabel}</span>
            <ChevronRight size={20} />
          </button>
        )
      )}

      {transitionReason && (
        <div
          className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2"
          data-testid="transition-reason"
        >
          {transitionReason}
        </div>
      )}

      <div className="flex items-center justify-between px-2 pt-2">
        <TimelineStep
          label={t("logger.progress.q1", "Q1")}
          active={status.progress === "q1" || status.progress === "break-q1"}
          completed={["q2", "halftime", "q3", "break-q3", "q4", "fulltime", "overtime", "break-ot", "show-all"].includes(status.progress)}
        />
        <TimelineConnector active={["q2", "halftime", "q3", "break-q3", "q4", "fulltime", "overtime", "break-ot", "show-all"].includes(status.progress)} />
        <TimelineStep
          label={t("logger.progress.q2", "Q2")}
          active={status.progress === "q2" || status.progress === "halftime"}
          completed={["q3", "break-q3", "q4", "fulltime", "overtime", "break-ot", "show-all"].includes(status.progress)}
        />
        <TimelineConnector active={["q3", "break-q3", "q4", "fulltime", "overtime", "break-ot", "show-all"].includes(status.progress)} />
        <TimelineStep
          label={t("logger.progress.q3", "Q3")}
          active={status.progress === "q3" || status.progress === "break-q3"}
          completed={["q4", "fulltime", "overtime", "break-ot", "show-all"].includes(status.progress)}
        />
        <TimelineConnector active={["q4", "fulltime", "overtime", "break-ot", "show-all"].includes(status.progress)} />
        <TimelineStep
          label={t("logger.progress.q4", "Q4")}
          active={status.progress === "q4" || status.progress === "fulltime"}
          completed={["overtime", "break-ot", "show-all"].includes(status.progress)}
        />
        {(isOvertime || ["overtime", "break-ot", "show-all"].includes(status.progress)) && (
          <>
            <TimelineConnector active={["overtime", "break-ot", "show-all"].includes(status.progress)} />
            <TimelineStep
              label={t("logger.progress.ot", "OT")}
              active={status.progress === "overtime" || status.progress === "break-ot"}
              completed={status.progress === "show-all"}
            />
          </>
        )}
      </div>

      <div className="text-xs text-gray-500 text-center">
        {status.stage === "completed"
          ? t("logger.period.matchCompletedInfo", "Match has ended. Review events or close session.")
          : t("logger.period.matchProgressInfo", "Control match flow above.")}
      </div>
    </div>
  );
}

function TimelineStep({
  label,
  active,
  completed,
}: {
  label: string;
  active: boolean;
  completed: boolean;
}) {
  return (
    <div
      className={`flex flex-col items-center gap-1 transition-colors duration-300 ${
        active
          ? "text-blue-600"
          : completed
            ? "text-slate-600"
            : "text-slate-300"
      }`}
    >
      <div
        className={`w-3 h-3 rounded-full border-2 ${
          active
            ? "bg-blue-600 border-blue-600"
            : completed
              ? "bg-slate-600 border-slate-600"
              : "bg-transparent border-slate-300"
        }`}
      />
      <span className="text-[10px] font-bold">{label}</span>
    </div>
  );
}

function TimelineConnector({ active }: { active: boolean }) {
  return (
    <div
      className={`h-0.5 flex-1 mx-2 rounded ${
        active ? "bg-slate-600" : "bg-slate-200"
      }`}
    />
  );
}
